// TODO: deprecate, use shared/carousel
import * as React from 'react';
import styled from 'styled-components';
import { media, breakpointSizes } from '@packages/ui';
import { isBrowser, debounce } from '@packages/common';

import { ArrowLeft } from './ArrowLeft/ArrowLeft';
import { ArrowRight } from './ArrowRight/ArrowRight';
import { ChildrenContainer, ShadowBoxBigRightShadow, OverflowHide } from './SimpleSliderStyles';

const MINIMAL_DRAG_TIME_MS = 600;
const MINIMAL_DRAG_PX = 5;

export interface ShownItems {
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
  xxl: number;
}

export interface SliderProps {
  shownItems: [number, number, number, number, number, number];
}

export const breakpoints = Object.keys(breakpointSizes) as (keyof typeof breakpointSizes)[];

export const SlideWrapper = styled.div<ShownItems>`
  ${media.to.sm`
    width: ${(props: ShownItems) => 100 / props.xs}%;
    min-width: ${(props: ShownItems) => 100 / props.xs}%;
  `}

  ${breakpoints.slice(0, 1).map(
    (key) => media.from[key]`
    width: ${(props: ShownItems) => 100 / (props[key] + 0.7)}%;
    min-width: ${(props: ShownItems) => 100 / (props[key] + 0.7)}%;
  `,
  )}

  ${breakpoints.slice(1, 3).map(
    (key) => media.from[key]`
    width: ${(props: ShownItems) => 100 / (props[key] - 0.2)}%;
    min-width: ${(props: ShownItems) => 100 / (props[key] - 0.2)}%;
  `,
  )}

  ${breakpoints.slice(3).map(
    (key) => media.from[key]`
    width: ${(props: ShownItems) => 100 / (props[key] - 0.8)}%;
    min-width: ${(props: ShownItems) => 100 / (props[key] - 0.8)}%;
  `,
  )}
  &:last-child {
    position: relative;
    &::after {
      position: absolute;
      content: '';
      ${media.to.md`
        right: -15px;
        width: 15px;
      `}
      right: -50px;
      width: 50px;
      height: 100%;
    }
  }
`;

export function getClientX(event: MouseEvent | TouchEvent): number {
  if ((event as any).touches) {
    return (event as TouchEvent).touches[0].clientX;
  }
  return (event as MouseEvent).clientX;
}

export function discardEvent(event: MouseEvent | TouchEvent): boolean {
  event.stopPropagation();
  event.preventDefault();
  return false;
}

export const destroyHolder = (
  holder: HTMLDivElement,
  preventClickIfDragged: (event: MouseEvent) => void,
  debouncedAdjustDisabledButtons: VoidFunction,
  start: (event: MouseEvent | TouchEvent) => void,
  stop: (event: MouseEvent | TouchEvent) => void,
  move: (event: MouseEvent | TouchEvent) => void,
  adjustDisabledButtons: VoidFunction,
) => {
  holder.removeEventListener('click', preventClickIfDragged);
  holder.removeEventListener('scroll', debouncedAdjustDisabledButtons);
  holder.removeEventListener('dragstart', discardEvent);
  holder.removeEventListener('mousedown', start);
  holder.removeEventListener('mousemove', move);
  holder.removeEventListener('mouseup', stop);
  holder.removeEventListener('mouseleave', stop);
  holder.removeEventListener('mouseout', stop);
  holder.removeEventListener('touchstart', start);
  holder.removeEventListener('touchmove', move);
  holder.removeEventListener('touchend', stop);
  holder.removeEventListener('touchcancel', stop);
  window.removeEventListener('resize', adjustDisabledButtons);
};

export const initializeHolder = (
  holderRef: React.MutableRefObject<HTMLDivElement | null>,
  childrenRef: React.MutableRefObject<HTMLDivElement | null>,
  lastEvent: React.MutableRefObject<MouseEvent | TouchEvent | null>,
  setArrowLeftDisabled: (state: boolean) => void,
  setArrowRightDisabled: (state: boolean) => void,
  // Design: Simple slider should have first item half-scrolled on mobile. DY slider ignores this logic
  scrollInitially = true,
) => {
  const holder = holderRef.current!;
  const firstChild = childrenRef.current!.children[0] as HTMLDivElement;
  // Need to distinguish between drag and click
  let lastStartTime = new Date();
  let lastDragDistance = 0;

  //On mobile, first element should be scrolled a bit
  if (
    scrollInitially &&
    isBrowser() &&
    window.innerWidth < Number(breakpointSizes.md.replace('px', '')) &&
    firstChild &&
    holder
  ) {
    holder.scrollLeft = firstChild.clientWidth / 2;
  }

  const start = (event: MouseEvent | TouchEvent) => {
    lastStartTime = new Date();
    lastEvent.current = event;
    lastDragDistance = 0;
  };

  const adjustDisabledButtons = () => {
    if (holder.scrollLeft <= 0) {
      setArrowLeftDisabled(true);
    } else {
      setArrowLeftDisabled(false);
    }

    if (holder.scrollLeft / (holder.scrollWidth - holder.clientWidth) >= 1) {
      setArrowRightDisabled(true);
    } else {
      setArrowRightDisabled(false);
    }
  };

  const debouncedAdjustDisabledButtons = debounce(adjustDisabledButtons, 100);

  const move = (event: MouseEvent | TouchEvent) => {
    if (!lastEvent.current || !holder) {
      return;
    }

    const xDiff = getClientX(event) - getClientX(lastEvent.current);
    holder.scrollLeft = holder.scrollLeft - xDiff;
    lastDragDistance += xDiff;

    adjustDisabledButtons();
    lastEvent.current = event;
  };
  const stop = (e: MouseEvent | TouchEvent) => {
    if (!(e && ['mouseout', 'mouseleave'].includes(e.type) && e.target !== holderRef.current)) {
      lastEvent.current = null;
    }
  };

  const preventClickIfDragged = (event: MouseEvent) => {
    if (Date.now() - lastStartTime.getTime() > MINIMAL_DRAG_TIME_MS || Math.abs(lastDragDistance) > MINIMAL_DRAG_PX) {
      event.stopPropagation();
      event.preventDefault();
      return false;
    }
    return true;
  };
  holder.addEventListener('scroll', debouncedAdjustDisabledButtons, { passive: true });
  holder.addEventListener('click', preventClickIfDragged);
  holder.addEventListener('dragstart', discardEvent);
  holder.addEventListener('mousedown', start);
  holder.addEventListener('mousemove', move);
  holder.addEventListener('mouseup', stop);
  holder.addEventListener('mouseleave', stop);
  holder.addEventListener('mouseout', stop);
  holder.addEventListener('touchstart', start);
  holder.addEventListener('touchmove', move);
  holder.addEventListener('touchend', stop);
  holder.addEventListener('touchcancel', stop);
  window.addEventListener('resize', adjustDisabledButtons);
  return () => {
    destroyHolder(
      holder,
      preventClickIfDragged,
      debouncedAdjustDisabledButtons,
      start,
      stop,
      move,
      adjustDisabledButtons,
    );
  };
};

export const onArrowClickFactory =
  (
    forward: boolean,
    holderRef: React.MutableRefObject<HTMLDivElement | null>,
    childrenRef: React.MutableRefObject<HTMLDivElement | null>,
    setArrowLeftDisabled: (state: boolean) => void,
    setArrowRightDisabled: (state: boolean) => void,
  ) =>
  (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    const measurableParent = holderRef.current!.parentNode as HTMLDivElement;
    // DY not activated
    if (!childrenRef.current) {
      return;
    }
    // DY inserts <style> as first element
    const firstChild = (childrenRef.current.children[1] || childrenRef.current.children[0]) as HTMLDivElement;
    if (!firstChild) {
      return;
    }
    const itemWidth = firstChild.clientWidth || firstChild.getBoundingClientRect().width;
    const fullWidth = measurableParent.clientWidth;
    const scrollLength = (fullWidth / itemWidth - 1) * itemWidth;
    if (forward) {
      setArrowLeftDisabled(false);
      if (holderRef.current!.scrollLeft + scrollLength >= holderRef.current!.clientWidth - fullWidth) {
        setArrowRightDisabled(true);
      }
    } else {
      setArrowRightDisabled(false);
      if (holderRef.current!.scrollLeft - scrollLength <= 0) {
        setArrowLeftDisabled(true);
      }
    }
    holderRef.current!.scrollBy
      ? holderRef.current!.scrollBy({
          top: 0,
          left: forward ? scrollLength : -scrollLength,
          behavior: 'smooth',
        })
      : (holderRef.current!.scrollLeft = holderRef.current!.scrollLeft + (forward ? scrollLength : -scrollLength));
  };

export const SimpleSlider: React.FC<SliderProps> = (props) => {
  const {
    shownItems: [xs, sm, md, lg, xl, xxl],
  } = props;
  const holderRef = React.useRef<HTMLDivElement | null>(null);
  const childrenRef = React.useRef<HTMLDivElement | null>(null);
  const [arrowLeftDisabled, setArrowLeftDisabled] = React.useState<boolean>(true);
  const [arrowRightDisabled, setArrowRightDisabled] = React.useState<boolean>(false);
  const lastEvent = React.useRef<MouseEvent | TouchEvent | null>(null);

  React.useEffect(() => {
    initializeHolder(holderRef, childrenRef, lastEvent, setArrowLeftDisabled, setArrowRightDisabled);
  }, []);

  return (
    <OverflowHide>
      <div ref={holderRef}>
        {!arrowRightDisabled && <ShadowBoxBigRightShadow />}
        <ChildrenContainer ref={childrenRef}>
          {React.Children.toArray(props.children).map((child, index) => (
            <SlideWrapper key={`${index}`} {...{ xs, sm, md, lg, xl, xxl }}>
              {child}
            </SlideWrapper>
          ))}
        </ChildrenContainer>
      </div>
      <React.Fragment>
        <ArrowLeft
          onClick={onArrowClickFactory(false, holderRef, childrenRef, setArrowLeftDisabled, setArrowRightDisabled)}
          disabled={arrowLeftDisabled}
        />
        <ArrowRight
          onClick={onArrowClickFactory(true, holderRef, childrenRef, setArrowLeftDisabled, setArrowRightDisabled)}
          disabled={arrowRightDisabled}
        />
      </React.Fragment>
    </OverflowHide>
  );
};
